import { supabase } from '../client'
import type { CloudProjectComment } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── PROJECT COMMENTS (Kommentare an Projekten, #184) ─────────────────── */

export async function getCloudProjectComments(userId: string, since?: Date): Promise<CloudProjectComment[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('project_comments')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('posted_at', { ascending: true })
    if (since) query = query.gte('updated_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get project comments error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get project comments error', error)
    return []
  }
}

export async function getCloudProjectCommentIds(userId: string): Promise<string[]> {
  if (!supabase) return []
  try {
    const { data, error } = await supabase
      .from('project_comments')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(100000)
    if (error) {
      logSupabaseError('Get project comment IDs error', error)
      return []
    }
    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get project comment IDs error', error)
    return []
  }
}

export async function getCloudProjectCommentsByIds(userId: string, ids: string[]): Promise<CloudProjectComment[]> {
  if (!supabase || ids.length === 0) return []
  try {
    const { data, error } = await supabase
      .from('project_comments')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)
    if (error) {
      logSupabaseError('Get project comments by IDs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get project comments by IDs error', error)
    return []
  }
}

/**
 * Weich gelöschte Kommentare seit `since`.
 *
 * `updated_at` steht mit Absicht in der Auswahl: `sync-pull` schiebt damit die
 * Wasserlinie weiter. Die älteren Entities lassen es weg, deren Linie bleibt
 * bei einem reinen Lösch-Fenster stehen und der nächste Pull holt es erneut —
 * verschwenderisch, nicht verlustig.
 */
export async function getDeletedCloudProjectComments(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('project_comments')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)
    if (since) query = query.gte('deleted_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get deleted project comments error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get deleted project comments error', error)
    return []
  }
}

export async function createCloudProjectComment(
  userId: string,
  comment: Omit<CloudProjectComment, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
): Promise<CloudProjectComment | null> {
  if (!supabase) return null
  try {
    const { data, error } = await supabase
      .from('project_comments')
      .insert({ ...comment, user_id: userId })
      .select()
      .single()
    if (error) {
      logSupabaseError('Create project comment error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Create project comment error', error)
    return null
  }
}

export async function updateCloudProjectComment(
  commentId: string,
  updates: Partial<Omit<CloudProjectComment, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null
  try {
    let query = supabase
      .from('project_comments')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', commentId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query.select('updated_at').single()
    if (error) {
      logSupabaseError('Update project comment error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Update project comment error', error)
    return null
  }
}

export async function deleteCloudProjectComment(commentId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false
  try {
    let query = supabase
      .from('project_comments')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', commentId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query
    if (error) {
      logSupabaseError('Delete project comment error', error)
      return false
    }
    return true
  } catch (error) {
    logSupabaseError('Delete project comment error', error)
    return false
  }
}
